import React, { useMemo } from "react";
import { CatLoaderInline } from "../../components/CatLoader";

// Функции для извлечения данных из материалов и доставки
const extractMaterialsData = (materials) => {
  if (!materials || materials.length === 0) {
    return {
      pricePerUnit: "—",
      volume: "—",
      totalMaterialsCost: 0
    };
  }

  // Рассчитываем объем
  const totalVolume = materials.reduce((sum, material) => {
    if (material.calculator === "Газобетон и кирпич") {
      const factVolume = material.factVolume && !isNaN(parseFloat(material.factVolume))
        ? parseFloat(material.factVolume)
        : null;
      return sum + (factVolume !== null ? factVolume : (parseFloat(material.quantity) || 0));
    }
    return sum + (parseFloat(material.quantity) || 1);
  }, 0);

  // Рассчитываем общую стоимость материалов
  const totalMaterialsCost = materials.reduce((sum, m) => {
    if (m.calculator === "Газобетон и кирпич") {
      const factVolume = m.factVolume && !isNaN(parseFloat(m.factVolume))
        ? parseFloat(m.factVolume)
        : null;
      if (factVolume !== null) {
        return Math.ceil(sum + m.pricePerOne * factVolume);
      }
      return Math.ceil(sum + m.pricePerOne * parseFloat(m.quantity));
    }
    return sum + Math.ceil(m.pricePerOne) * (parseFloat(m.quantity) || 1);
  }, 0);

  // Средняя цена за единицу
  const pricePerUnit = totalVolume > 0 ? Math.ceil(totalMaterialsCost / totalVolume) : 0;

  return {
    pricePerUnit: pricePerUnit > 0 ? pricePerUnit.toLocaleString() : "—",
    volume: totalVolume > 0 ? (totalVolume % 1 === 0 ? totalVolume.toString() : totalVolume.toFixed(2).replace(/\.?0+$/, "")) + " м³" : "—",
    totalMaterialsCost,
    volumeNumber: totalVolume
  };
};

const extractDeliveryData = (delivery, categoriesToInclude = ["ner"]) => {
  if (!delivery) {
    return {
      distance: "—",
      costPerTrip: "—",
      numberOfTrips: "—",
      totalDeliveryCost: 0
    };
  }

  const validDeliveries = categoriesToInclude
    .map((category) => {
      const categoryData = delivery[category];
      const details = categoryData?.details;
      if (!details) return null;

      if (Array.isArray(details)) {
        if (details.length === 0) return null;
        const totalCost = details.reduce((sum, group) => {
          return sum + (group?.deliveryData?.data?.totalCost || 0);
        }, 0);
        const totalTrips = details.reduce((sum, group) => {
          return sum + (group?.deliveryData?.data?.numberOfTrips || 0);
        }, 0);
        return {
          category,
          totalCost,
          numberOfTrips: totalTrips,
          costPerTrip: totalTrips > 0 ? Math.ceil(totalCost / totalTrips) : 0,
          distance: details[0]?.deliveryData?.data?.distanceProdToClient || details[0]?.deliveryData?.data?.distance || 0
        };
      } else {
        const data = details.data || details;
        return {
          category,
          totalCost: data?.totalCost || 0,
          numberOfTrips: data?.numberOfTrips || 0,
          costPerTrip: data?.costPerTrip || 0,
          distance: data?.distanceProdToClient || data?.distance || 0
        };
      }
    })
    .filter(Boolean);

  if (validDeliveries.length === 0) {
    return {
      distance: "—",
      costPerTrip: "—",
      numberOfTrips: "—",
      totalDeliveryCost: 0
    };
  }

  const delivery_data = validDeliveries[0]; // Берем первую валидную доставку
  return {
    distance: delivery_data.distance > 0 ? delivery_data.distance.toFixed(2) : "—",
    costPerTrip: delivery_data.costPerTrip > 0 ? delivery_data.costPerTrip.toLocaleString() : "—",
    numberOfTrips: delivery_data.numberOfTrips > 0 ? delivery_data.numberOfTrips : "—",
    totalDeliveryCost: delivery_data.totalCost || 0
  };
};

const ProductionsTableNonmetallic = ({
  isValidInput,
  isLoading,
  sortedGroupedData,
  handleSelectOffer,
  handleMouseEnter,
  handleMouseLeave,
  tooltip,
  tableContainerRef,
  tooltipRef,
  selfPickUp,
  mkrFilter = { min: 0.5, max: 4.0 },
  kfFilter = { min: 0.5, max: 20.0 },
}) => {

  // Функция для определения является ли материал песком
  const isSand = (material) => {
    if (!material) return false;
    const name = material.name?.toLowerCase() || "";
    const category = material.category?.toLowerCase() || "";
    // Проверяем по названию (содержит "песок") или по категории
    return name.includes("песок") || category.includes("песок");
  };

  // Сначала проверяем, есть ли песок в исходных данных (для показа столбцов)
  const hasSandInData = (sortedGroupedData || []).some(entry =>
    entry.materialsToShow?.some(material => isSand(material))
  );

  // Фильтруем данные по mkr и kf (только для песка)
  const filteredData = useMemo(() => {
    if (!hasSandInData) return sortedGroupedData || [];

    // Парсим min/max из mkrFilter
    const mkrMin = mkrFilter.min !== null && mkrFilter.min !== undefined
      ? parseFloat(String(mkrFilter.min).replace(',', '.')) || 0.5
      : 0.5;
    const mkrMax = mkrFilter.max !== null && mkrFilter.max !== undefined
      ? parseFloat(String(mkrFilter.max).replace(',', '.')) || 4.0
      : 4.0;

    // Парсим min/max из kfFilter
    const kfMin = kfFilter.min !== null && kfFilter.min !== undefined
      ? parseFloat(String(kfFilter.min).replace(',', '.')) || 0.5
      : 0.5;
    const kfMax = kfFilter.max !== null && kfFilter.max !== undefined
      ? parseFloat(String(kfFilter.max).replace(',', '.')) || 20.0
      : 20.0;

    return (sortedGroupedData || []).filter(entry => {
      const sandMaterial = entry.materialsToShow?.find(material => isSand(material));
      if (!sandMaterial) return true; // Если нет песка в этой строке, оставляем

      // --- Фильтр по Мкр ---
      let mkrValue = sandMaterial.mkr;
      let mkrInRange = true; // Если Мкр не задан, не фильтруем по нему
      if (sandMaterial.mkr !== null && sandMaterial.mkr !== undefined) {
        if (typeof mkrValue === 'string') {
          const firstNum = mkrValue.replace(',', '.').split(/[-–—]/)[0];
          mkrValue = parseFloat(firstNum) || 0;
        } else {
          mkrValue = parseFloat(mkrValue) || 0;
        }
        mkrInRange = mkrValue >= mkrMin && mkrValue <= mkrMax;
      }

      // --- Фильтр по Кф ---
      let kfValue = sandMaterial.kf;
      let kfInRange = true; // Если Кф не задан, не фильтруем по нему
      if (sandMaterial.kf !== null && sandMaterial.kf !== undefined) {
        if (typeof kfValue === 'string') {
          const firstNum = kfValue.replace(',', '.').split(/[-–—]/)[0];
          kfValue = parseFloat(firstNum) || 0;
        } else {
          kfValue = parseFloat(kfValue) || 0;
        }
        kfInRange = kfValue >= kfMin && kfValue <= kfMax;
      }

      // Оба фильтра должны выполняться (AND)
      return mkrInRange && kfInRange;
    });
  }, [sortedGroupedData, hasSandInData, mkrFilter, kfFilter]);

  return (
    <div ref={tableContainerRef} style={{ position: "relative", width: "100%", borderRadius: "12px", overflow: "hidden", border: "1px solid #aab6c9", boxShadow: "0 1px 3px rgba(2, 2, 23, 0.05)" }}>
      <div className="rd-nerudka-scroll" style={{ maxHeight: "520px", overflowY: "auto", overflowX: "auto" }}>
      <table className="table table-hover rd-nerudka-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{width: "3%"}}>№</th>
            <th className="rd-nerudka-th--text" rowSpan={2} style={{width: selfPickUp ? "22%" : "16%"}}>Производство</th>
            {!selfPickUp && <th rowSpan={2} style={{width: "5%"}}>Расст., км</th>}
            {hasSandInData && (
              <>
                <th rowSpan={2} style={{width: "4%"}}>Мкр</th>
                <th rowSpan={2} style={{width: "4%"}}>Кф</th>
              </>
            )}
            <th colSpan={3} className="rd-nerudka-group rd-nerudka-group--tovar">ТОВАР</th>
            {!selfPickUp && <th colSpan={3} className="rd-nerudka-group rd-nerudka-group--delivery">ДОСТАВКА</th>}
            <th colSpan={2} className="rd-nerudka-group rd-nerudka-group--total">ОБЩЕЕ</th>
          </tr>
          <tr>
            <th className="rd-nerudka-sub rd-nerudka-sub--tovar rd-nerudka-sub--group-start" style={{width: "5%"}}>Объём</th>
            <th className="rd-nerudka-sub rd-nerudka-sub--tovar" style={{width: selfPickUp ? "12%" : "9%"}}>Цена за м³, ₽</th>
            <th className="rd-nerudka-sub rd-nerudka-sub--tovar" style={{width: selfPickUp ? "12%" : "9%"}}>Сумма, ₽</th>
            {!selfPickUp && <th className="rd-nerudka-sub rd-nerudka-sub--delivery rd-nerudka-sub--group-start" style={{width: "5%"}}>Рейсы</th>}
            {!selfPickUp && <th className="rd-nerudka-sub rd-nerudka-sub--delivery" style={{width: "9%"}}>За рейс, ₽</th>}
            {!selfPickUp && <th className="rd-nerudka-sub rd-nerudka-sub--delivery" style={{width: "9%"}}>Сумма, ₽</th>}
            <th className="rd-nerudka-sub rd-nerudka-sub--total rd-nerudka-sub--group-start" style={{width: selfPickUp ? "15%" : "9%"}}>Цена за м³, ₽</th>
            <th className="rd-nerudka-sub rd-nerudka-sub--total" style={{width: selfPickUp ? "20%" : "9%"}}>Итого, ₽</th>
          </tr>
        </thead>
        <tbody>
          {!isValidInput ? (
            <tr>
              <td
                colSpan={selfPickUp ? (hasSandInData ? 10 : 8) : (hasSandInData ? 14 : 12)}
                className="text-center"
              >
                Нет данных
              </td>
            </tr>
          ) : isLoading ? (
            <tr>
              <td
                colSpan={selfPickUp ? (hasSandInData ? 10 : 8) : (hasSandInData ? 14 : 12)}
                className="text-center"
              >
                {document.querySelector('.app-catgirl') ? (
                  <CatLoaderInline />
                ) : (
                  <>
                    <span>Загрузка...</span>{" "}
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </>
                )}
              </td>
            </tr>
          ) : filteredData.length > 0 ? (
            filteredData.map((entry, index) => {
              const { comparison, material, materialsToShow, totalMaterialCostForDisplay, totalSum } = entry;

              const uniqueSources = Array.from(
                new Set(
                  (materialsToShow || [])
                    .map((m) => m.source)
                    .filter((source) => source && source !== "-")
                )
              );
              const allSources = uniqueSources.length > 0 ? uniqueSources.join(", ") : "-";

              // Извлекаем данные для новых столбцов
              const materialsData = extractMaterialsData(materialsToShow);
              const deliveryData = extractDeliveryData(entry.deliveryForTooltip, entry.categoriesToInclude);

              // Отладка mkr
              const sandMaterial = materialsToShow?.find(m => isSand(m));
              // Форматируем mkr и kf для отображения
              const formatValue = (val) => {
                if (val === null || val === undefined) return '—';
                if (typeof val === 'string') return val; // уже строка как "1,3" или "2-3"
                return String(val).replace('.', ',');
              };
              // Отладка данных для тултипа
              console.log('🏭 Production info for', comparison.production, ':', comparison.productionInfo);
              console.log('🏭 ADDITIONAL_INFO:', comparison.productionInfo?.ADDITIONAL_INFO);

              // Рассчитываем итоговые значения
              const totalCost = materialsData.totalMaterialsCost + deliveryData.totalDeliveryCost;
              const costPerUnitWithDelivery = materialsData.volumeNumber > 0 && totalCost > 0
                ? Math.ceil(totalCost / materialsData.volumeNumber)
                : 0;

              return (
                <tr key={entry.id} >
                  <td className="rd-nerudka-idx">{index + 1}</td>
                  <td
                    className="rd-nerudka-td--text rd-nerudka-link"
                    onMouseEnter={(e) => {
                      const additionalInfo = comparison.productionInfo?.ADDITIONAL_INFO;
                      console.log('🏭 handleMouseEnter called with:', additionalInfo);
                      handleMouseEnter(e, additionalInfo);
                    }}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleSelectOffer(comparison, entry)}
                  >
                    {comparison.production}
                  </td>
                  {!selfPickUp && (
                    <td>{parseInt(deliveryData.distance)}</td>
                  )}
                  {hasSandInData && (
                    <>
                      <td>{formatValue(sandMaterial?.mkr)}</td>
                      <td>{formatValue(sandMaterial?.kf)}</td>
                    </>
                  )}
                  <td className="rd-nerudka-edge rd-nerudka-num rd-nerudka-col--tovar">{materialsData.volume}</td>
                  <td className="rd-nerudka-num rd-nerudka-col--tovar">{materialsData.pricePerUnit}</td>
                  <td className="rd-nerudka-num rd-nerudka-col--tovar">
                    {materialsData.totalMaterialsCost > 0 ? materialsData.totalMaterialsCost.toLocaleString() : "—"}
                  </td>
                  {!selfPickUp && (
                    <td className="rd-nerudka-edge rd-nerudka-col--delivery">{deliveryData.numberOfTrips}</td>
                  )}
                  {!selfPickUp && (
                    <td className="rd-nerudka-num rd-nerudka-col--delivery">{deliveryData.costPerTrip}</td>
                  )}
                  {!selfPickUp && (
                    <td className="rd-nerudka-num rd-nerudka-col--delivery">
                      {deliveryData.totalDeliveryCost > 0 ? deliveryData.totalDeliveryCost.toLocaleString() : "—"}
                    </td>
                  )}
                  <td className="rd-nerudka-edge rd-nerudka-num rd-nerudka-col--total">
                    {costPerUnitWithDelivery > 0 ? costPerUnitWithDelivery.toLocaleString() : "—"}
                  </td>
                  <td className="rd-nerudka-num rd-nerudka-col--total rd-nerudka-total">
                    {totalCost > 0 ? totalCost.toLocaleString() : "—"}
                  </td>
                  {/* <td style={{textAlign: "center", verticalAlign: "middle", fontSize: "13px", padding: "8px 4px"}} >
                    1800
                  </td> */}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={selfPickUp ? (hasSandInData ? 10 : 8) : (hasSandInData ? 14 : 12)}
                className="text-center"
              >
                Нет данных
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {tooltip.visible && (
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            top: tooltip.y,
            left: tooltip.x,
            backgroundColor: "#333",
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
            fontSize: "12px",
            pointerEvents: "none",
            zIndex: 1000,
            whiteSpace: "nowrap",
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.text?.replace(/\n/g, "<br/>") }}
        />
      )}
    </div>
  );
};

export default ProductionsTableNonmetallic;
